import { FLIP, RENDERER_MODE, SEATSTATUS, SECTION, SECTIONSTATUS, TYPE } from '@/constants';
import { CategoryMixin } from '@/core/mixins/CategoryMixin';
import { SelectableMixin } from '@/core/mixins/SelectableMixin';
import store from '@/store/store';
import findKey from 'lodash/findKey';
import SVG from 'svg.js';
import eventbus from 'tiny-emitter/instance';

import Model, { ModelParams } from '../model';
import { Block } from '../block';
import { Seat } from '../seat';
import { GA } from '../ga/ga';
import { TableSeat } from '../tables/tableSeat';
import { POS } from '@/store/modules/pos';
import { reactive } from 'vue';
import { useActions, useState } from '@/store/useStore';

export class Section extends CategoryMixin(SelectableMixin(Model)) {
  scx = 0;
  scy = 0;
  minHOffset = 0;
  maxHOffset = 0;
  minVOffset = 0;
  maxVOffset = 0;
  insidesShown = false;
  declare subtype: (typeof TYPE)[keyof typeof TYPE];
  dragCoords?: { sx: number; sy: number; sdx?: number; sdy?: number } = { sx: 0, sy: 0 };
  rotateMatrices?: { view: SVG.Matrix } = { view: new SVG.Matrix() };
  invalidServerSection = false;
  shapeResizeHandlers: null | SVG.Set = null;
  actions = useActions('seatmap');
  seatmapStore = reactive(useState('seatmap'));

  backup: any = null;

  declare label: SVG.Text;

  constructor(params: ModelParams) {
    super(params);
    this.isInside = false;
    this.canvas = this.app.canvas;

    this.syncWithServerSection();
    this.loadThumbnail();

    this.init();
  }

  syncWithServerSection(sectionId = this.attributes.serverSectionId) {
    this.attributes.serverSectionId = sectionId;
    if (this.app.props.sections && sectionId) {
      // find server section
      const serverSection = this.app.props.sections.items.find((s) => s.id === sectionId);

      if (!serverSection) {
        this.invalidServerSection = true;
        this.view?.addClass('invalid-server-section');
        return;
      }

      const serverCategory = this.app.props.categoriesList.find((c) => c.id === serverSection.categoryId);
      const isGa = serverCategory?.type === TYPE.GA;

      this.view?.removeClass('invalid-server-section');

      // make sure the section is in sync with the server section
      this.attributes.entrance = serverSection.entrance ?? this.attributes.entrance;
      this.attributes.level = serverSection.level ?? this.attributes.level;
      // this.attributes.label.text = serverSection.name ?? this.attributes.label.text;
      // this.attributes.categoryId = serverSection.categoryId ?? this.attributes.categoryId;
      this.attributes.sectionCode = serverSection.code ?? this.attributes.sectionCode;
      this.attributes.ga = serverCategory ? isGa : this.attributes.ga;

      serverSection.categoryId && this.assignCategory(serverSection.categoryId);
      serverSection.name && this.changeLabel(serverSection.name);
    }

    if (this.app.props.sections && sectionId === null) {
      this.assignCategory(null);
    }
  }

  loadThumbnail() {
    // Dont load thumbnail if section already has one
    if (this.attributes.thumbnail) return;

    if (!this.app.props.sectionPreviews) return;

    const { thumbnail } = this.app.props.sectionPreviews.find((s) => s.id == this.uuid) ?? {};

    if (!thumbnail) return;

    this.attributes.thumbnail = thumbnail;
  }

  init() {
    this.view = this.canvas
      .group()
      .addClass(this.type)
      .addClass('sectionstatus-' + findKey(SECTIONSTATUS, (s) => s === this.status)?.toLowerCase());

    if (this.attributes.invisible) {
      this.addClass('inv');
    }

    if (this.invalidServerSection) {
      this.addClass('invalid-server-section');
    }
  }

  get blocks() {
    return this.app.seatmap.blocks.filter((block) => block.sid === this.uuid);
  }

  get predefineds() {
    return this.app.seatmap.predefinedsFromSection(this.uuid);
  }

  get type() {
    return TYPE.SECTION;
  }

  get visible() {
    return this.view.visible();
  }

  set visible(value) {
    value === true ? this.show() : this.hide();
  }

  show() {
    this.view.show();
    this.label.show();
    // if (this.category && this.category.color) {
    //   this.changeFillColor(this.category.color, false);
    // } else if (this.attributes.color) {
    //   this.changeFillColor(this.attributes.color, false);
    // }
    this.toFront();
    // this.updateConflictedZone();
  }

  hide() {
    this.view.hide();
    this.label.hide();
    this.toBack();
    // this.updateConflictedZone();
  }

  initLabel() {
    const { label, shapecenter } = this.attributes;
    const { text, size, fill } = label;
    if (shapecenter.length === 0) {
      const { cx, cy } = this.shapeRBox();
      this.scx = cx;
      this.scy = cy;
      this.attributes.shapecenter = [cx, cy];
    } else {
      this.scx = shapecenter[0];
      this.scy = shapecenter[1];
    }
    this.label = this.canvas
      .plain(text)
      .font({ size, family: 'Roboto', style: 'normal' })
      .attr({
        fill,
        x: this.scx,
        y: this.scy,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      })
      .addClass('label');
    this.view.add(this.label);
    this.changeLabelTransform();

    if (this.app.props.isHideSectionLabels) {
      this.hideLabel();
    }
  }

  changeLabelTransform() {
    let { hOffset, vOffset, labeltransform } = this.attributes.label;
    hOffset = hOffset === '' ? 0 : hOffset;
    vOffset = vOffset === '' ? 0 : vOffset;
    labeltransform = labeltransform === '' ? undefined : labeltransform;
    const { horizontalFlip, verticalFlip } = this.attributes;
    const { cx, cy } = this.label.rbox(this.view);
    let lm = new SVG.Matrix().translate(hOffset, vOffset).multiply(labeltransform);
    lm = lm.multiply(new SVG.Matrix().rotate(this.attributes.label.rotation, this.scx, this.scy));
    if (this.subtype === TYPE.RECT) {
      if (horizontalFlip === -1) {
        lm = lm.multiply(new SVG.Matrix().rotate(180, this.scx, this.scy));
      }
    } else if (this.subtype === TYPE.POLYGON || this.subtype === TYPE.PATH) {
      if (horizontalFlip === -1 && verticalFlip !== -1) {
        const posC = cy;
        lm = lm.multiply(new SVG.Matrix().flip(FLIP.HORIZONTALLY, posC));
      } else if (verticalFlip === -1 && horizontalFlip !== -1) {
        const posC = cx;
        lm = lm.multiply(new SVG.Matrix().flip(FLIP.VERTICALLY, posC));
      } else if (verticalFlip === -1 && horizontalFlip === -1) {
        const posCx = cx;
        lm = lm.multiply(new SVG.Matrix().flip(FLIP.VERTICALLY, posCx));
        const posCy = cy;
        lm = lm.multiply(new SVG.Matrix().flip(FLIP.HORIZONTALLY, posCy));
      }
    }
    this.label.transform(lm as any);
    this.changeLabelProp('transform', this.label.transform('matrix' as any));
  }

  assignCategory(categoryId: number | null | undefined) {
    eventbus.emit('section:category:assign', this, categoryId);
  }

  unassignCategory() {
    eventbus.emit('section:category:unassign', this.uuid);
  }

  assignPos(pos: POS) {
    eventbus.emit('section:pos:assign', this.uuid, pos);
  }

  unassignPos() {
    eventbus.emit('section:pos:unassign', this.uuid);
  }

  shapeRBox(parent: SVG.Element = this.svg) {
    return this.shape.rbox(parent);
  }

  recalculateEdgeOffsets() {
    const { w, h } = this.shapeRBox();
    const halfHLen = w / 2;
    const halfVLen = h / 2;
    this.minHOffset = -halfHLen;
    this.maxHOffset = halfHLen;
    this.minVOffset = -halfVLen;
    this.maxVOffset = halfVLen;
  }

  inside() {
    this.isInside = true;
    this.view.addClass('inside');
    this.view.attr({
      fill: this.app.getGridPattern(),
    });
    this.hideLabel();
    this.showSectionUnits();
    eventbus.emit('tooltip:hide', this);
    // this.app.seatmap.unitsFromSection(this.uuid).forEach((e) => e.view.front());
    //this.view.back();
    // this.updateConflictedZone();
  }

  outside() {
    this.hideSectionUnits();
    this.isInside = false;
    this.view.removeClass('inside');
    this.showLabel();
    // this.app.seatmap.unitsFromSection(this.uuid).forEach((e) => e.view.back());
    //this.view.front();
  }

  updateConflictedZone() {
    if (this.app.conflictedzones?.includes(this.uuid)) {
      this.shape.addClass('conflicted-section');
    } else {
      this.shape.removeClass('conflicted-section');
    }
  }
  removeBorder() {
    this.shape.removeClass('hover-zone');
  }
  addBorder() {
    this.shape.addClass('hover-zone');
  }
  highlight() {
    this.shape.addClass('highlight-zone');
  }
  unhighlight() {
    this.shape.removeClass('highlight-zone');
  }
  darkenColor() {
    this.shape.addClass('darken-zone');
  }
  undarkenColor() {
    this.shape.removeClass('darken-zone');
  }
  whitenColor() {
    this.shape.addClass('whiten-zone');
  }
  unwhitenColor() {
    this.shape.removeClass('whiten-zone');
  }
  markCurrentCategoryZones() {
    let hasCurrentCategorySeats = false;
    const seats: (Seat | GA | TableSeat)[] = [];
    this.app.seatmap.unitsFromSection(this.uuid).forEach((u) => {
      if (u.type === TYPE.BLOCK) {
        u.param_rows.forEach((r) => {
          seats.push(...r.seats);
        });
      } else if (u.type === TYPE.TABLE) {
        seats.push(...u.seats);
      } else if (u.type === TYPE.GA) {
        seats.push(u);
      }
    });
    seats.forEach((s) => {
      // TODO: This doesnt work anymore
      if (!this.app.categoryFilterationFail(s)) {
        hasCurrentCategorySeats = true;
      }
    });

    if (
      hasCurrentCategorySeats ||
      (this.app.props.categoryID && Number(this.attributes.categoryId) === Number(this.app.props.categoryID))
    ) {
      const alpha = parseFloat(this.attributes.color.replace(/^.*,(.+)\)/, '$1'));
      if (alpha <= 0.02) {
        this.shape.addClass('marked-zone-animate');
      } else {
        this.shape.addClass('filled-zone-animate');
      }
    }
    return hasCurrentCategorySeats;
  }

  showSectionUnits() {
    this.isInside = true;
    if (this.category) {
      this.removeCategoryColor();
    }
    eventbus.emit('section:units:show', this.uuid);
  }

  showSectionUnits2() {
    this.isInside = true;
    if (this.category) {
      this.removeCategoryColor();
    }
  }

  hideSectionUnits() {
    this.isInside = false;
    if (this.category) {
      this.applyCategoryColor(this.category.color);
    }
    eventbus.emit('section:units:hide', this.uuid);
  }

  hideSectionUnits2() {
    this.isInside = false;
    if (this.category) {
      this.applyCategoryColor(this.category.color);
    }
  }

  hideLabel() {
    this.label.hide();
  }

  showLabel() {
    if (this.app.props.isHideSectionLabels) return;
    this.label.show();
  }

  toFront() {
    this.view.front();
    this.label.front();
    if (this.app.seatmap.store.splitModeUnit === this.uuid) {
      this.showInsides();
    }
  }

  toBack() {
    this.view.back();
    this.label.back();
  }

  changeLabelProp(name: string, value: any) {
    if (value === this.attributes.label[name]) return;
    this.attributes.label[name] = value;
  }

  changeLabel(value = '') {
    if (!this.label) return;
    value = value.trim();
    this.changeLabelProp('text', value);
    this.label.plain(value);
    eventbus.emit('selection:delayedHistory', 'Section label change');
  }

  changeLabelStroke(color: string, snapshot = true) {
    if (!color) return;
    this.changeLabelProp('stroke', color);
    this.changeLabelProp('fill', color);
    this.label.style({ fill: color, stroke: color });
    if (snapshot) eventbus.emit('selection:delayedHistory', 'Section label color');
  }

  changeFillColor(color: string, snapshot = true) {
    if (!color) return;
    this.view.removeClass('section-shape');
    // const alpha = parseFloat(color.replace(/^.*,(.+)\)/, '$1'));
    if (this.app.props.bgImage) {
      this.view.attr({
        fill: color,
      });
    } else {
      this.view.attr({
        fill: color,
        stroke: SECTION.DEFAULTSTROKECOLOR,
      });
    }
    this.attributes.color = color;
    if (snapshot) eventbus.emit('selection:delayedHistory', 'Section fill');
  }

  changeOpacity(value: number, snapshot = true) {
    this.attributes.opacity = value;

    this.shape.attr({
      opacity: value / 100,
    });
    if (snapshot) eventbus.emit('selection:delayedHistory', 'Section opacity');
  }

  addClass(lclass: string) {
    this.view.addClass(lclass);
  }

  changeHOffset(offset: number) {
    this.changeLabelProp('hOffset', offset);
    this.changeLabelTransform();
    eventbus.emit('selection:delayedHistory', 'Section horizontal reposition');
  }

  changeVOffset(offset: number) {
    this.changeLabelProp('vOffset', offset);
    this.changeLabelTransform();
    eventbus.emit('selection:delayedHistory', 'Section vertical reposition');
  }

  changeFontSize(size: number) {
    this.changeLabelProp('size', size);
    this.label.font({ size });
    eventbus.emit('selection:delayedHistory', 'Section label font');
  }

  changeScale(value: number, oldValue: number, initialLoad = false, alreadyApplied = false) {
    if (value !== 0 && !alreadyApplied) {
      value /= 100;
      oldValue /= 100;
      const newVal = value / oldValue;
      // if (!this.attributes.scale) {
      //   const { cx, cy } = this.shapeRBox();
      //   this.attributes.shapecenter = [cx, cy];
      //   this.attributes.position = [cx, cy];
      // }
      const matrix = this.view.transform('matrix' as any) as unknown as SVG.Matrix;
      const sm = new SVG.Matrix().scale(newVal, 0, 0);
      this.view.transform(sm.multiply(matrix) as any);
      if (!initialLoad) {
        this.app.seatmap.unitsFromSection(this.uuid).forEach((u) => {
          if ('seats' in u && u.seats.length === 0) return;

          const { cx: ucx, cy: ucy } = u.rbox();
          const dx = ucx * (newVal - 1);
          const dy = ucy * (newVal - 1);
          const utm = new SVG.Matrix().translate(dx, dy);
          const um = u.view.transform('matrix' as any) as unknown as SVG.Matrix;
          u.view.transform(utm.multiply(um) as any);
        });
        this.attributes.scale = value;
      }
      eventbus.emit('selection:updated', this.app.seatmap.selection);
    }
    //eventbus.emit('selection:delayedHistory', 'Section scale');
  }

  changeRotation(rotation: number) {
    this.changeLabelProp('rotation', rotation);
    this.changeLabelTransform();
    eventbus.emit('selection:delayedHistory', 'Section label rotate');
  }

  onDragStart() {
    if (this.insidesShown) return;
    const draggable = !!store.state.seatmap.isDraggable;
    if (draggable) {
      eventbus.emit('section:drag:start', this.uuid);
      const { x: sx, y: sy } = this.view.transform();
      this.dragCoords = { sx: sx!, sy: sy! };
      this.view.addClass('no-fill');
    }
  }

  onDragMove(dx: number, dy: number) {
    const draggable = !!store.state.seatmap.isDraggable;
    if (draggable && this.dragCoords) {
      this.dragCoords.sdx = this.dragCoords.sx + dx;
      this.dragCoords.sdy = this.dragCoords.sy + dy;
      this.view.transform({ x: this.dragCoords.sdx, y: this.dragCoords.sdy });
    }
  }

  onDragEnd(dx: number, dy: number, dragged: boolean) {
    const draggable = !!store.state.seatmap.isDraggable;
    if (draggable) {
      this.view.removeClass('no-fill');
      eventbus.emit('section:drag:end', this.uuid, dx, dy, dragged);
      delete this.dragCoords;
    }
  }

  onRotateStart() {
    eventbus.emit('section:rotate:start', this.uuid);
    this.rotateMatrices = {
      view: this.view.transform('matrix' as any) as unknown as SVG.Matrix,
    };
  }

  onRotateMove(rm: SVG.Matrix) {
    this.view.transform(rm.multiply(this.rotateMatrices!.view) as any);
  }

  onRotateEnd(rm: number, angle: number) {
    eventbus.emit('section:rotate:end', this.uuid, rm, angle);
    delete this.rotateMatrices;
    this.attributes.rotation = (this.attributes.rotation ?? 0) + angle;
  }

  toJSON() {
    const { uuid, type } = this;
    const attributes = Object.assign({}, this.attributes, {
      transform: this.view.transform('matrix' as any) as unknown as SVG.Matrix,
    });
    attributes.label.transform = this.label.transform('matrix' as any);
    attributes.shapetransform = this.shape.transform('matrix' as any);
    return Section.filteredJSON({
      type,
      uuid,
      attributes,
    });
  }

  destroy(force = false) {
    if (this.app.seatmap.store.splitModeUnit === this.uuid) {
      this.app.seatmap.actions.disableSplitMode();
    }
    const blocks: Block[] = this.app.seatmap.blocks.filter((block) => block.sid === this.uuid);

    let booked = 0;
    //check seats in each block, if seat is occupied, then block cannot be removed
    blocks.forEach((b) => {
      b.seats.forEach((s) => {
        if (s.attributes.status === SEATSTATUS.OCCUPIED || s.attributes.status === SEATSTATUS.RESERVED || s.inCart) {
          booked++;
        }
      });
    });
    if (!force && booked > 0) {
      return;
    }
    this.label.remove();
    this.view.remove();
    eventbus.emit('section:removed', this);
  }

  get status() {
    return this.attributes.status ?? SECTIONSTATUS.AVAILABLE;
  }

  set status(value) {
    this.attributes.status = value;
  }

  markSelection() {
    if (RENDERER_MODE && (this.category?.bestAvailable || this.attributes.ga)) {
      this.view.addClass('section-selected');
    }
  }

  unmarkSelection() {
    if (RENDERER_MODE && (this.category?.bestAvailable || this.attributes.ga)) {
      this.view.removeClass('section-selected');
    }
  }

  hideInsides() {
    this.label.show();
    // if (this.type === TYPE.SECTION && this.subtype === TYPE.POLYGON) {
    //   this.attributes.smoothing = this.resizedUnitSmoothing;
    //   this.plot();
    // }
    eventbus.emit('section:units:unlockPositionsAndHideInBack', this.uuid);
    this.insidesShown = false;
  }

  showInsides() {
    this.label.hide();
    eventbus.emit('section:units:lockPositionsAndShowInFront', this.uuid);
    // if (this.type === TYPE.SECTION && this.subtype === TYPE.POLYGON) {
    //   this.setResizedUnitSmoothing(this.attributes.smoothing);
    //   this.attributes.smoothing = 0;
    //   this.plot(true);
    // }

    this.insidesShown = true;
  }

  makeVisible() {
    this.attributes.invisible = false;
    this.view.removeClass('inv');
  }

  makeInvisible() {
    this.attributes.invisible = true;
    this.view.addClass('inv');
  }

  bbox(): SVG.BBox {
    throw new Error('Method not implemented.');
  }

  endResizing() {
    if (this.shapeResizeHandlers) {
      // @ts-expect-error
      this.shape.selectize().resize('stop');
      // @ts-expect-error
      this.shapeResizeHandlers.addClass('resize-handler').addClass('hide');

      this.hideInsides();

      this.actions.setResizing(false);
      this.actions.setResizedUnit(null);
      this.actions.setResizedUnitSmoothing(null);
    }

    const { enableSelection } = useActions('settings');
    enableSelection();
    this.app.seatmap.selection.add(this);
    eventbus.emit('selection:updated', this.app.seatmap.selection);
  }

  startResizing(whileCb?: () => void, doneCb?: () => void) {
    if (this.app.seatmap.store.resizedUnit) {
      this.app.seatmap.getSectionById(this.app.seatmap.store.resizedUnit)?.endResizing();
    }

    const { disableSelection } = useActions('settings');

    this.app.seatmap.selection.purge();
    eventbus.emit('selection:updated', this.app.seatmap.selection);
    disableSelection();

    this.actions.setResizing(true);
    // @ts-expect-error
    this.shape.selectize().resize().on('resizing', this.onResizing, this);

    if (whileCb) {
      this.shape.on('resizing', whileCb);
    }

    if (doneCb) {
      this.shape.on('resizedone', doneCb);
    }

    this.shapeResizeHandlers = this.view.select('g');

    // @ts-expect-error
    if (this.shapeResizeHandlers && this.shapeResizeHandlers.members.length > 0) {
      // @ts-expect-error
      this.shapeResizeHandlers.addClass('resize-handler').removeClass('hide');
    }

    this.actions.setResizedUnit(this.uuid);
    this.showInsides();
  }

  onResizing() {
    this.app.seatmap.triggerUnsavedChanges(true);

    // @ts-expect-error
    if (!this.shapeResizeHandlers || this.shapeResizeHandlers.members.length === 0) {
      this.shapeResizeHandlers = this.view.select('g');
    }
    this.recalculateEdgeOffsets();

    const { cx, cy, width } = this.shapeRBox(this.view);
    this.attributes.shapecenter = [cx, cy];
    this.scx = cx;
    this.scy = cy;

    if (this.subtype === TYPE.CIRCLE) {
      this.attributes.radius = width / 2;
      this.attributes.position = [cx, cy];
    } else if (this.subtype === TYPE.RECT) {
      this.attributes.width = this.shape.attr('width');
      this.attributes.height = this.shape.attr('height');
    } else if (this.subtype === TYPE.POLYGON) {
      const shape = (this.shape as SVG.Polygon).array().value.map((coord) => {
        // @ts-expect-error
        return [coord[1], coord[2]];
      });

      this.attributes.points = (this.shape as SVG.Polygon).array();
      this.attributes.shape.value = shape;
    }

    this.label.attr({
      x: cx,
      y: cy,
    });
  }

  edit() {
    this.backupShape();
    this.startResizing();
  }

  backupShape() {
    throw new Error('Method not implemented.');
  }
}
